type MinimalUser = {
  app_metadata?: unknown;
  created_at?: string;
  email?: string | null;
  id: string;
  last_sign_in_at?: string | null;
  user_metadata?: unknown;
};

type SupabaseLike = {
  from: (table: string) => any;
};

const requireRows = async (query: Promise<{ data: unknown[] | null; error: { message: string } | null }>) => {
  const { data, error } = await query;

  if (error) {
    throw error;
  }

  return data ?? [];
};

const requireSingle = async (query: Promise<{ data: unknown; error: { message: string } | null }>) => {
  const { data, error } = await query;

  if (error) {
    throw error;
  }

  return data ?? null;
};

export const buildAccountDataExport = async ({
  supabase,
  user,
}: {
  supabase: SupabaseLike;
  user: MinimalUser;
}) => {
  const [
    profile,
    children,
    posts,
    replies,
    feedPosts,
    feedComments,
    adventuresCreated,
    adventureMembers,
    connections,
    directMessages,
    chatRoomMessages,
    eventRsvps,
  ] = await Promise.all([
    requireSingle(supabase.from("profiles").select("*").eq("id", user.id).maybeSingle()),
    requireRows(supabase.from("children").select("*").eq("user_id", user.id)),
    requireRows(supabase.from("posts").select("*").eq("user_id", user.id)),
    requireRows(supabase.from("replies").select("*").eq("user_id", user.id)),
    requireRows(supabase.from("feed_posts").select("*").eq("user_id", user.id)),
    requireRows(supabase.from("feed_comments").select("*").eq("user_id", user.id)),
    requireRows(supabase.from("adventures").select("*").eq("user_id", user.id)),
    requireRows(supabase.from("adventure_members").select("*").eq("user_id", user.id)),
    requireRows(
      supabase
        .from("connections")
        .select("*")
        .or(`requester_id.eq.${user.id},recipient_id.eq.${user.id}`),
    ),
    requireRows(supabase.from("direct_messages").select("*").eq("sender_id", user.id)),
    requireRows(supabase.from("chat_room_messages").select("*").eq("user_id", user.id)),
    requireRows(supabase.from("event_rsvps").select("*").eq("user_id", user.id)),
  ]);

  return {
    exported_at: new Date().toISOString(),
    account: {
      id: user.id,
      email: user.email ?? null,
      created_at: user.created_at ?? null,
      last_sign_in_at: user.last_sign_in_at ?? null,
      app_metadata: user.app_metadata ?? null,
      user_metadata: user.user_metadata ?? null,
    },
    tables: {
      profiles: profile,
      children,
      posts,
      replies,
      feed_posts: feedPosts,
      feed_comments: feedComments,
      adventures_created: adventuresCreated,
      adventure_members: adventureMembers,
      connections,
      direct_messages: directMessages,
      chat_room_messages: chatRoomMessages,
      event_rsvps: eventRsvps,
    },
  };
};
